import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedDataSetSecurity } from '../Shared/ManagedDataSet';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    SlimUserAdminModel,
    SlimTenantAdminModel,
    mapSlimUserAdminModel,
    mapSlimTenantAdminModel,
} from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';

export type ManagedDataSetSecurityEditResult = {
    operation: ManagedAdminChildOperation;
    dataSetSecurity: ManagedDataSetSecurity;
};

type AUDDataSetSecurityFormValues = {
    dssUserID: number;
    dssTenantID: number;
    dssUserRoleID: number | null;
    dssAdditionalWhere: string | null;
};

type AUDDataSetSecuritiesProps = {
    dataSetID: number;
    dataSetName: string;
    dataSetSecurities: ManagedDataSetSecurity[];
    initialSelectedDataSetSecurityID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetDataSetSecurity: (
        result: ManagedDataSetSecurityEditResult
    ) => void;
    close: () => void;
};

const AUDDataSetSecurities: React.FC<AUDDataSetSecuritiesProps> = ({ close, ...props }) => {
    const { user } = useAuth();
    const [formAUDDataSetSecurity] = Form.useForm();

    const [dssUsersIncludeDisabled] = useState(false);
    const [dssTenantsIncludeDisabled] = useState(false);

    const dataSetSecurities = props.dataSetSecurities ?? [];

    const getInitialDSSMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedDataSetSecurity = (): ManagedDataSetSecurity | undefined => {
        if (!props.initialSelectedDataSetSecurityID || props.initialSelectedDataSetSecurityID <= 0) {
            return undefined;
        }

        return dataSetSecurities.find(
            (dataSetSecurity) => dataSetSecurity.id === props.initialSelectedDataSetSecurityID
        );
    };

    const buildDataSetSecurityNameConstruct = (dss: ManagedDataSetSecurity | undefined) => {
        const tenantName =
            dss?.tenantId && dss.tenantId > 0
                ? (tenants.find((tenant) => tenant.id === dss.tenantId)?.name ?? '')
                : '';

        const selectedUser =
            dss?.userId && dss.userId > 0
                ? users.find((user) => user.id === dss.userId)
                : undefined;

        const userName =
            selectedUser
                ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim()
                : '';

        return 'DSID: ' + (dss?.dataSetId ?? 0) +
            ' / TID: [' + (dss?.tenantId ?? 0) + '] ' + tenantName +
            ' / UID: [' + (dss?.userId ?? 0) + '] ' + userName;
    };

    const initialSelectedDataSetSecurity = getInitialSelectedDataSetSecurity();
    const initialDSSMode = getInitialDSSMode();

    const initialDSSFormValues = initialDSSMode === 'add'
        ? {
            dssUserID: 0,
            dssTenantID: 0,
            dssUserRoleID: null,
            dssAdditionalWhere: '',
        }
        : {
            dssUserID: initialSelectedDataSetSecurity?.userId ?? 0,
            dssTenantID: initialSelectedDataSetSecurity?.tenantId ?? 0,
            dssUserRoleID: initialSelectedDataSetSecurity?.userRoleId ?? null,
            dssAdditionalWhere: initialSelectedDataSetSecurity?.additionalWhere ?? '',
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialDSSMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialDSSMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialDSSMode === 'delete');

    const [dssSelectedDataSetSecurityID, setDSSSelectedDataSetSecurityID] = useState(initialSelectedDataSetSecurity?.id ?? -1);
    const [dssSelectedDataSetSecurityName, setDSSSelectedDataSetSecurityName] = useState(
        initialSelectedDataSetSecurity ? buildDataSetSecurityNameConstruct(initialSelectedDataSetSecurity) : ''
    );

    const [dssUserID, setDSSUserID] = useState<number>(initialSelectedDataSetSecurity?.userId ?? 0);
    const [dssTenantID, setDSSTenantID] = useState<number>(initialSelectedDataSetSecurity?.tenantId ?? 0);
    const [dssUserRoleID, setDSSUserRoleID] = useState<number | null>(initialSelectedDataSetSecurity?.userRoleId ?? null);

    const [dssSelectedDataSetSecurityCreatedBy, setDSSSelectedDataSetSecurityCreatedBy] = useState(initialSelectedDataSetSecurity?.createdBy ?? -1);
    const [dssSelectedDataSetSecurityCreatedTs, setDSSSelectedDataSetSecurityCreatedTs] = useState(initialSelectedDataSetSecurity?.createdTs ?? new Date());

    const dssPropsDataSetID = props.dataSetID > 0 ? props.dataSetID : 0;
    const dssPropsDataSetName = props.dataSetName !== '' ? props.dataSetName : 'New DataSet';

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredDSSField = (fieldName: string): Promise<void> => {
        const value = formAUDDataSetSecurity.getFieldValue(fieldName);
        return isBlank(value) || value === 0 ? Promise.reject() : Promise.resolve();
    };

    const validateDSSDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'dssUserID':
                return Promise.resolve();

            case 'dssTenantID':
                return validateRequiredDSSField(rule.field);

            case 'dssUserRoleID':
            case 'dssAdditionalWhere':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const { data: users = [], isLoading: isGetUsersLoading } = useQuery<GetUserAdminModelsResponse[], Error, SlimUserAdminModel[]>(
        ['getUserAdminModelsRequest', dssUsersIncludeDisabled],
        async (): Promise<GetUserAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = { gua_includeDisabled: dssUsersIncludeDisabled };
                return await AxiosUtil.get<GetUserAdminModelsResponse[], GetUserAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleDSSLoadError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleDSSLoadError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error', err);
            },
            select: (response: GetUserAdminModelsResponse[]): SlimUserAdminModel[] => {
                return response
                    .map(mapSlimUserAdminModel)
                    .filter((eachUser) => !eachUser.deleted);
            },
        }
    );

    const { data: tenants = [], isLoading: isGetTenantsLoading } = useQuery<GetTenantAdminModelsResponse[], Error, SlimTenantAdminModel[]>(
        ['getTenantAdminModelsRequest', dssTenantsIncludeDisabled],
        async (): Promise<GetTenantAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS}`;
                const requestData: GetTenantAdminModelsRequest = { gta_includeDisabled: dssTenantsIncludeDisabled };
                return await AxiosUtil.get<GetTenantAdminModelsResponse[], GetTenantAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleDSSLoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleDSSLoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
            },
            select: (response: GetTenantAdminModelsResponse[]): SlimTenantAdminModel[] => {
                return response
                    .map(mapSlimTenantAdminModel)
                    .filter((eachTenant) => !eachTenant.deleted);
            },
        }
    );

    const handleDSSUserIDChange = (value: number) => {
        setDSSUserID(value);
    };

    const handleDSSTenantIDChange = (value: number) => {
        setDSSTenantID(value);
    };

    const handleDSSUserRoleIDChange = (value: number | null | undefined) => {
        setDSSUserRoleID(value ?? null);
    };

    const buildManagedDataSetSecurityRequest = (values: AUDDataSetSecurityFormValues): ManagedDataSetSecurity => {
        return {
            id: addButtonClicked ? 0 : (updButtonClicked ? dssSelectedDataSetSecurityID : (delButtonClicked ? dssSelectedDataSetSecurityID : 0)),
            dataSetId: dssPropsDataSetID,
            userRoleId: values.dssUserRoleID ?? null,
            userId: values.dssUserID === 0 ? null : values.dssUserID,
            additionalWhere: values.dssAdditionalWhere === undefined || values.dssAdditionalWhere === null || String(values.dssAdditionalWhere).trim() === '' ? null : values.dssAdditionalWhere,
            tenantId: values.dssTenantID,
            createdBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? dssSelectedDataSetSecurityCreatedBy : (delButtonClicked ? dssSelectedDataSetSecurityCreatedBy : 0)),
            modifiedBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? (user?.id ?? 0) : (delButtonClicked ? (user?.id ?? 0) : 0)),
            deletedBy: addButtonClicked ? 0 : (updButtonClicked ? 0 : (delButtonClicked ? (user?.id ?? 0) : 0)),
            createdTs: addButtonClicked ? (new Date()) : (updButtonClicked ? dssSelectedDataSetSecurityCreatedTs : (delButtonClicked ? dssSelectedDataSetSecurityCreatedTs : undefined)),
            modifiedTs: addButtonClicked ? (new Date()) : (updButtonClicked ? (new Date()) : (delButtonClicked ? (new Date()) : undefined)),
            deletedTs: addButtonClicked ? (undefined) : (updButtonClicked ? (undefined) : (delButtonClicked ? (new Date()) : undefined)),
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDDataSetSecurityFormValues>) => {
        console.error('AUDDataSetSecurities validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'DataSet Security Validation',
                'Please correct the highlighted DataSet Security fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDDataSetSecurityFormValues) => {
        const newManagedDataSetSecurity = buildManagedDataSetSecurityRequest(values);

        props.handleSetDataSetSecurity({
            operation: getDSSOperation(),
            dataSetSecurity: newManagedDataSetSecurity,
        });

        close();
    };

    const setDSSMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const clearDSSSelection = () => {
        setDSSSelectedDataSetSecurityID(-1);
        setDSSSelectedDataSetSecurityName('');
        setDSSSelectedDataSetSecurityCreatedBy(0);
        setDSSSelectedDataSetSecurityCreatedTs(new Date());

        setDSSUserID(0);
        setDSSTenantID(0);
        setDSSUserRoleID(null);

        setDSSMode('add');
        handleDSSDataSetSecurityResetForm('blank');
    };

    const handleDSSSelectionChange = (value: number) => {
        setDSSSelectedDataSetSecurityID(value);

        const selectedDataSetSecurity = dataSetSecurities.find((dataSetSecurity) => (dataSetSecurity.id === value));

        setDSSSelectedDataSetSecurityName(buildDataSetSecurityNameConstruct(selectedDataSetSecurity));

        setDSSSelectedDataSetSecurityCreatedBy(selectedDataSetSecurity?.createdBy ?? 0);
        setDSSSelectedDataSetSecurityCreatedTs(selectedDataSetSecurity?.createdTs ?? new Date());

        setDSSMode('add');
        handleDSSDataSetSecurityResetForm('blank');
    };

    const setDSSBlankForm = () => {
        formAUDDataSetSecurity.setFieldValue('dssUserID', 0);
        formAUDDataSetSecurity.setFieldValue('dssTenantID', 0);
        formAUDDataSetSecurity.setFieldValue('dssUserRoleID', null);
        formAUDDataSetSecurity.setFieldValue('dssAdditionalWhere', '');

        setDSSUserID(0);
        setDSSTenantID(0);
        setDSSUserRoleID(null);
    };

    const setDSSFormFromSelectedDataSetSecurity = () => {
        const selectedDataSetSecurity = dataSetSecurities.find(
            (dataSetSecurity) => dataSetSecurity.id === dssSelectedDataSetSecurityID
        );

        formAUDDataSetSecurity.setFieldValue('dssUserID', selectedDataSetSecurity?.userId ?? 0);
        formAUDDataSetSecurity.setFieldValue('dssTenantID', selectedDataSetSecurity?.tenantId ?? 0);
        formAUDDataSetSecurity.setFieldValue('dssUserRoleID', selectedDataSetSecurity?.userRoleId ?? null);
        formAUDDataSetSecurity.setFieldValue('dssAdditionalWhere', selectedDataSetSecurity?.additionalWhere ?? '');

        setDSSUserID(selectedDataSetSecurity?.userId ?? 0);
        setDSSTenantID(selectedDataSetSecurity?.tenantId ?? 0);
        setDSSUserRoleID(selectedDataSetSecurity?.userRoleId ?? null);
    };

    const getDSSOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getDSSActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const handleDSSDataSetSecurityResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setDSSBlankForm();
            return;
        }

        setDSSFormFromSelectedDataSetSecurity();
    };

    //=================================================================
    //=================================================================
    
    const handleDSSAddDataSetSecurity = () => {
        clearDSSSelection();
    };

    //=================================================================
    //=================================================================

    const handleDSSUpdDataSetSecurity = () => {
        setDSSMode('update');
        handleDSSDataSetSecurityResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleDSSDelDataSetSecurity = () => {
        setDSSMode('delete');
        handleDSSDataSetSecurityResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onDSSCancel = () => {
        clearDSSSelection();
    };

    const handleDSSLoadError = (title: string, err: unknown) => {
        const apiError = err as ManagedApiError;

        const message =
            apiError?.response?.data?.message
                ? `${title}: ${apiError.response.data.message}`
                : `${title}: ${String(err)}`;

        console.error(title, err);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'DataSet Security Load Error',
                message,
                true
            )
        );
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };
    
    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <Spin spinning={ isGetUsersLoading || isGetTenantsLoading }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage DataSet Securities</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'DataSet: [' + dssPropsDataSetID + '] ' + dssPropsDataSetName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'DataSet Security: [' + dssSelectedDataSetSecurityID + '] ' + dssSelectedDataSetSecurityName}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select DataSet Security to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a DataSet Security'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleDSSSelectionChange}
                        >
                            {dataSetSecurities.map(
                                (eachDataSetSecurity) => {
                                    return (
                                        <Option
                                            key={eachDataSetSecurity.id}
                                            label={buildDataSetSecurityNameConstruct(eachDataSetSecurity)}
                                            value={eachDataSetSecurity.id}
                                        >
                                            {buildDataSetSecurityNameConstruct(eachDataSetSecurity)}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={5}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSSAddDataSetSecurity}
                        >
                            Add DataSet Security
                        </Button>
                    </Col>
                    <Col span={8} />
                    <Col span={5}>
                        <Button
                            disabled={dssSelectedDataSetSecurityID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSSUpdDataSetSecurity}
                        >
                            Update DataSet Security
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={5}>
                        <Button
                            disabled={dssSelectedDataSetSecurityID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSSDelDataSetSecurity}
                        >
                            Delete DataSet Security
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDDataSetSecurity}
                    initialValues={initialDSSFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getDSSActionColor() }}>
                                    {addButtonClicked ? 'Add DataSet Security' :
                                        (updButtonClicked ? 'Update DataSet Security: [' + dssSelectedDataSetSecurityID + '] ' :
                                            (delButtonClicked ? 'Delete DataSet Security: [' + dssSelectedDataSetSecurityID + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getDSSActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleDSSDataSetSecurityResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='dssUserID'
                                        rules={[
                                            {
                                                validator: validateDSSDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='User ID:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={dssUserID}
                                            onChange={handleDSSUserIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {users.map(
                                                (eachUser) => {
                                                    return (
                                                        <Option
                                                            key={eachUser.id}
                                                            label={eachUser.firstName + ' ' + eachUser.lastName}
                                                            value={eachUser.id}
                                                        >
                                                            {eachUser.firstName + ' ' + eachUser.lastName}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='dssTenantID'
                                        rules={[
                                            {
                                                message: 'Tenant required.',
                                                required: true,
                                                validator: validateDSSDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='Tenants:'
                                            optionFilterProp='label'
                                            placeholder='Select Tenant'
                                            showSearch
                                            value={dssTenantID}
                                            onChange={handleDSSTenantIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {tenants.map(
                                                (eachTenant) => {
                                                    return (
                                                        <Option
                                                            key={eachTenant.id}
                                                            label={eachTenant.name}
                                                            value={eachTenant.id}
                                                        >
                                                            {eachTenant.name}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={6}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='dssuserroleid'
                                        name='dssUserRoleID'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='User Role ID:'
                                            placeholder='Select User Role ID'
                                            showSearch
                                            value={dssUserRoleID}
                                            onChange={handleDSSUserRoleIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            <Option
                                                value={1}
                                                label={'1'}
                                            >
                                                {'1 Admin'}
                                            </Option>
                                            <Option
                                                value={2}
                                                label={'2'}
                                            >
                                                {'2 Manager'}
                                            </Option>
                                            <Option
                                                value={3}
                                                label={'3'}
                                            >
                                                {'3 Nurse'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={1} />
                            <Col span={17}>
                                <Form.Item
                                    key='dssadditionalwhere'
                                    label='Additional Where'
                                    labelCol={{ span: 5, }}
                                    name='dssAdditionalWhere'
                                    rules={[
                                        {
                                            validator: validateDSSDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px', marginTop: '26px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Additional Where'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={5}>
                            <Form.Item
                                name='dssCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={9} />
                        <Col span={5}>
                            <Form.Item
                                name='dssApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add DataSet Security' : (updButtonClicked ? 'Update DataSet Security' : (delButtonClicked ? 'Delete DataSet Security' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name='dssCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onDSSCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDDataSetSecurities;
